import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";
import { useAuth } from "../context/AuthContext";
import MicroMissionModal from "../components/missions/MicroMissionModal";

/* =========================
   Analytics
========================= */
async function trackEvent(event) {
  try {
    const session = JSON.parse(localStorage.getItem("zedro_session"));

    await fetch(`${API_URL}/api/analytics/event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.token}`,
      },
      body: JSON.stringify(event),
    });
  } catch (err) {
    console.error("analytics error", err);
  }
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M5 12.5l4.2 4.2L19 7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatInlineBold(text = "") {
  const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function renderEditorialText(content = "", tone = "default") {
  const normalized = String(content || "")
    .replace(/\r\n/g, "\n")
    .trim();
  if (!normalized) return null;

  const sections = normalized
    .split(/\n\s*---+\s*\n/g)
    .map((section) => section.trim())
    .filter(Boolean);

  const iconMap = {
    book: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M5 4.5h10.5A3.5 3.5 0 0 1 19 8v11.5H8.5A3.5 3.5 0 0 1 5 16V4.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M8.5 4.5A3.5 3.5 0 0 0 5 8v8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M8.5 8h6.5M8.5 11h5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),

    check: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M5 12.5l4.2 4.2L19 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),

    arrow: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M5 12h13M13 6l6 6-6 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),

    light: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M12 3v3M12 18v3M4.8 4.8l2.1 2.1M17.1 17.1l2.1 2.1M3 12h3M18 12h3M4.8 19.2l2.1-2.1M17.1 6.9l2.1-2.1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <circle
          cx="12"
          cy="12"
          r="3.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    ),

    heart: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M12 20s-7-4.5-8.8-9.2C2 7.8 3.8 5.5 6.5 5.5c1.6 0 3 0.9 3.8 2.2.8-1.3 2.2-2.2 3.8-2.2 2.7 0 4.5 2.3 3.3 5.3C19 15.5 12 20 12 20Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    ),

    crown: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M4.5 8.5l4.2 3.2L12 6l3.3 5.7 4.2-3.2-1.6 9H6.1l-1.6-9Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M6.5 19h11"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),

    pen: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M4 20l4.4-1 9.8-9.8a2.1 2.1 0 0 0-3-3L5.4 16 4 20Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M13.8 7.6l2.6 2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  };

  let numberCounter = 0;

  return (
    <div
      className={`z-editorial z-editorial--${tone} ${sections.length > 1 ? "has-sections" : ""}`}
    >
      {sections.map((section, sectionIndex) => {
        const lines = section
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);

        if (lines.length === 0) return null;

        const firstLine = lines[0];
        const isScripture =
          sectionIndex === 0 &&
          (firstLine.startsWith("[[SCRIPTURE_REFERENCE]]") ||
            firstLine.includes("Génesis") ||
            firstLine.includes("Salmo") ||
            firstLine.includes("Mateo") ||
            firstLine.includes("Juan") ||
            firstLine.includes("Romanos") ||
            firstLine.includes("Proverbios"));

        const bodyLines = isScripture ? lines.slice(1) : lines;
        const scriptureTitle = isScripture
          ? firstLine
              .replace("[[SCRIPTURE_REFERENCE]]", "")
              .trim()
              .replace(/^>\s*/, "")
          : "";

        numberCounter = 0;

        return (
          <section
            key={sectionIndex}
            className={`z-editorial-section ${
              isScripture ? "is-scripture" : "is-application"
            }`}
          >
            {isScripture && (
              <div className="z-editorial-label">Palabra base</div>
            )}
            {!isScripture && sectionIndex > 0 && (
              <div className="z-editorial-label">Aplicación</div>
            )}

            {isScripture && (
              <h3 className="z-editorial-scriptureTitle">
                {formatInlineBold(scriptureTitle)}
              </h3>
            )}

            <div className="z-editorial-copy">
              {bodyLines.map((line, lineIndex) => {
                const clean = line.replace(/^>\s*/, "").trim();

                if (!clean) return null;

                if (clean.startsWith("[[QUOTE]]")) {
                  const quoteRaw = clean.replace("[[QUOTE]]", "").trim();
                  const quoteIconMatch = quoteRaw.match(
                    /^\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/,
                  );

                  let quoteIcon = null;
                  let quoteText = quoteRaw;

                  if (quoteIconMatch) {
                    quoteIcon = quoteIconMatch[1];
                    quoteText = quoteRaw
                      .replace(
                        /^\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/,
                        "",
                      )
                      .trim();
                  }

                  return (
                    <div key={lineIndex} className="z-editorial-quote">
                      {quoteIcon && (
                        <span className="z-editorial-inlineIcon">
                          {iconMap[quoteIcon]}
                        </span>
                      )}
                      {formatInlineBold(quoteText)}
                    </div>
                  );
                }

                const bulletMatch = clean.match(
                  /^\[\[BULLET:(dot|check|dash|number)\]\]\s*/,
                );
                const iconMatch = clean.match(
                  /\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]/,
                );

                if (bulletMatch) {
                  const bulletType = bulletMatch[1];
                  const iconType = iconMatch?.[1] || null;

                  let text = clean
                    .replace(/^\[\[BULLET:(dot|check|dash|number)\]\]\s*/, "")
                    .replace(
                      /\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/,
                      "",
                    )
                    .trim();

                  if (bulletType === "number") {
                    numberCounter += 1;
                  }

                  return (
                    <div
                      key={lineIndex}
                      className={`z-editorial-bullet is-${bulletType}`}
                    >
                      <span className="z-editorial-bulletMark">
                        {bulletType === "dot" && "•"}
                        {bulletType === "check" && "✓"}
                        {bulletType === "dash" && "—"}
                        {bulletType === "number" && `${numberCounter}.`}
                      </span>

                      <span className="z-editorial-bulletText">
                        {iconType && (
                          <span className="z-editorial-inlineIcon">
                            {iconMap[iconType]}
                          </span>
                        )}
                        {formatInlineBold(text)}
                      </span>
                    </div>
                  );
                }

                const paragraphIconMatch = clean.match(
                  /^\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/,
                );

                if (paragraphIconMatch) {
                  const paragraphIcon = paragraphIconMatch[1];
                  const paragraphText = clean
                    .replace(
                      /^\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/,
                      "",
                    )
                    .trim();

                  return (
                    <p key={lineIndex} className="z-editorial-paragraph">
                      <span className="z-editorial-inlineIcon">
                        {iconMap[paragraphIcon]}
                      </span>
                      {formatInlineBold(paragraphText)}
                    </p>
                  );
                }

                return (
                  <p key={lineIndex} className="z-editorial-paragraph">
                    {formatInlineBold(clean)}
                  </p>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function getLessonBlockView(block) {
  if (!block?.content) {
    return {
      body: "",
      microMission: null,
      buttonLabel: "Pasar a la acción",
    };
  }

  try {
    const parsed = JSON.parse(block.content);

    if (parsed?.kind === "lesson_block") {
      const microMissionFeature = parsed.features?.microMission;

      return {
        body: parsed.body || "",
        microMission:
          microMissionFeature?.enabled && microMissionFeature?.mission
            ? microMissionFeature.mission
            : null,
        buttonLabel: microMissionFeature?.buttonLabel || "Pasar a la acción",
      };
    }

    if (parsed?.kind === "micro_mission_block") {
      return {
        body: parsed.visible?.text || "",
        microMission: parsed.mission || null,
        buttonLabel: parsed.visible?.buttonLabel || "Pasar a la acción",
      };
    }

    if (parsed?.kind === "micro_mission") {
      return {
        body: "Hoy vas a aplicar esta verdad en una acción concreta. No necesitas resolverlo todo. Solo necesitas dar un paso real.",
        microMission: parsed,
        buttonLabel: "Pasar a la acción",
      };
    }
  } catch (err) {
    // Contenido normal antiguo.
  }

  return {
    body: block.content || "",
    microMission: null,
    buttonLabel: "Pasar a la acción",
  };
}

export default function SystemDay() {
  const { dayId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [missionOpen, setMissionOpen] = useState(false);

  function closeMissionAndReturnToTop() {
    setMissionOpen(false);

    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    });
  }

  async function completeMission(answers = {}) {
    if (data?.access_mode === "read_only") {
      throw new Error("Esta sesión está disponible solo para consulta.");
    }

    const session = JSON.parse(localStorage.getItem("zedro_session"));

    const res = await fetch(
      `${API_URL}/api/systems/day/${dayId}/mission/complete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
          "X-User-Timezone":
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            "America/Montreal",
        },
        body: JSON.stringify({
          answers,
        }),
      },
    );

    const json = await res.json();

    if (!res.ok || !json.ok) {
      throw new Error(json.error || "No se pudo guardar la micro-misión");
    }

    setData((current) => ({
      ...current,
      mission_progress: json.mission_progress,
      mission_summary: json.mission_summary,
      mission_day_summary: json.mission_day_summary,
    }));

    return json;
  }

  const { refreshSession } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const session = JSON.parse(localStorage.getItem("zedro_session"));

        const res = await fetch(`${API_URL}/api/systems/day/${dayId}`, {
          headers: {
            Authorization: `Bearer ${session?.token}`,
            "X-User-Timezone":
              Intl.DateTimeFormat().resolvedOptions().timeZone ||
              "America/Montreal",
          },
        });

        const json = await res.json();
        setData(json);

        if (json?.lesson?.id) {
          trackEvent({
            event_type: "lesson_open",
            entity_type: "lesson",
            entity_id: json.lesson.id,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [dayId]);

  async function markCompleted() {
    if (data?.access_mode === "read_only") return;

    try {
      setSubmitting(true);

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/systems/day/${dayId}/complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.token}`,
          "X-User-Timezone":
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            "America/Montreal",
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "Error guardando progreso");
      }

      await refreshSession();
      navigate(systemBackRoute, { replace: true });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [dayId]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [step]);

  if (loading) return <div style={{ padding: 40 }}>Cargando...</div>;
  if (!data?.ok) return <div style={{ padding: 40 }}>Error</div>;

  const lesson = data?.lesson;
  const blocks = data?.blocks || [];
  const progress = data?.progress || {};
  const isCompleted = progress?.completed;
  const isReadOnly = data?.access_mode === "read_only";

  const block = blocks?.[step];

  const blockView = getLessonBlockView(block);
  const microMissionData = blockView.microMission || null;
  const missionCompleted = Boolean(data?.mission_progress?.id);

  const progressPercent =
    blocks.length > 0 ? Math.round(((step + 1) / blocks.length) * 100) : 0;

  function next() {
    if (step < blocks.length - 1) setStep(step + 1);
  }

  function prev() {
    if (step > 0) setStep(step - 1);
  }

  const weekNumber =
    data?.system && lesson?.section_id
      ? (data.system.sections || []).findIndex(
          (section) => section.id === lesson.section_id,
        ) + 1
      : null;

  const dayLabel =
    lesson?.day_number && weekNumber
      ? `Semana ${weekNumber} · Día ${lesson.day_number}`
      : lesson?.day_number
        ? `Día ${lesson.day_number}`
        : "Sesión";
  const stepLabel = blocks.length > 0 ? `${step + 1} de ${blocks.length}` : "—";
  const systemBackRoute = data?.system?.id
    ? `/core/system/${data.system.id}`
    : lesson?.system_id
      ? `/core/system/${lesson.system_id}`
      : "/core";

  const currentTone = (() => {
    const title = (block?.title || "").toLowerCase();

    if (title.includes("audio") || title.includes("momento")) return "hero";
    if (title.includes("alineamiento")) return "soft";
    if (
      title.includes("ejecución") ||
      title.includes("haz que las cosas sucedan")
    ) {
      return "action";
    }
    if (title.includes("reino")) return "kingdom";
    if (title.includes("reflexión")) return "reflection";

    switch ((block?.type || "").toLowerCase()) {
      case "audio":
        return "hero";
      case "download":
        return "action";
      default:
        return "default";
    }
  })();

  function renderBlockContent(currentBlock) {
    if (!currentBlock) return null;

    const view = getLessonBlockView(currentBlock);
    const contentToRender = view.body;

    const legacyContentAsUrl = /^https?:\/\//.test(
      String(currentBlock.content || "").trim(),
    )
      ? String(currentBlock.content || "").trim()
      : "";

    const mediaUrl = currentBlock.media_url || legacyContentAsUrl;

    if (currentBlock.type === "audio") {
      return (
        <>
          {currentBlock.media_url && (
            <div className="z-day-audioWrap">
              <audio controls className="z-day-audio">
                <source src={currentBlock.media_url} type="audio/mpeg" />
              </audio>
            </div>
          )}

          {contentToRender && (
            <div className="z-day-copyWrap">
              {renderEditorialText(contentToRender, "hero")}
            </div>
          )}
        </>
      );
    }

    if (currentBlock.type === "video" && mediaUrl) {
      return (
        <div className="z-day-videoWrap">
          <video src={mediaUrl} controls className="z-day-video" />
        </div>
      );
    }

    if (currentBlock.type === "download" && mediaUrl) {
      return (
        <div className="z-day-downloadWrap">
          {contentToRender && renderEditorialText(contentToRender, currentTone)}

          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="z-day-downloadBtn"
          >
            Descargar recurso
            <ArrowIcon />
          </a>
        </div>
      );
    }

    return renderEditorialText(contentToRender, currentTone);
  }

  return (
    <main className="z-day-shell">
      <div className="z-day-backRow">
        <button
          type="button"
          className="z-day-backBtn"
          onClick={() => navigate(systemBackRoute)}
        >
          ← Volver al sistema
        </button>
      </div>

      <section className="z-day-hero">
        <div className="z-day-kicker">
          {isReadOnly ? "Consulta" : "ZEDRO Core"}
        </div>

        <div className="z-day-topline">
          <span className="z-day-label">{dayLabel}</span>
          <span className="z-day-step">{stepLabel}</span>
        </div>

        <h1 className="z-day-title">{lesson?.title}</h1>

        <p className="z-day-subtitle">
          {isReadOnly
            ? "Revisa el contenido y el trabajo que ya construiste. Esta consulta no modifica tu avance ni crea nuevas acciones."
            : "Avanza paso a paso. Cada bloque fue diseñado para ayudarte a integrar el contenido con intención y claridad."}
        </p>

        <div className="z-day-progressWrap">
          <div className="z-day-progressMeta">
            <span>{isReadOnly ? "Posición en la sesión" : "Progreso del día"}</span>
            <span>{progressPercent}%</span>
          </div>

          <div className="z-day-progressBar">
            <div
              className="z-day-progressFill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </section>

      <section className={`z-day-stage is-${currentTone}`}>
        <div className="z-day-stageHeader">
          <div className="z-day-stageKicker">Bloque actual</div>

          <h2 className="z-day-stageTitle">{block?.title || "Contenido"}</h2>
        </div>

        <div className="z-day-stageBody">
          {renderBlockContent(block)}

          {microMissionData && (
            <div className="z-mission-entry">
              {missionCompleted ? (
                <div className="z-mission-successCard" role="status">
                  <div className="z-mission-successIcon" aria-hidden="true">
                    <CheckIcon />
                  </div>

                  <div className="z-mission-successContent">
                    <strong>Acción registrada</strong>
                    <p>
                      {isReadOnly
                        ? "Este registro forma parte del avance que ya construiste y permanece disponible para revisión."
                        : "Tu trabajo quedó guardado en Bitácora. Continúa con el siguiente bloque del recorrido."}
                    </p>
                  </div>
                </div>
              ) : isReadOnly ? (
                <p className="z-card-copy z-card-copy--compact" role="status">
                  Esta actividad está disponible como referencia. En modo consulta no se crean nuevos registros ni se modifica tu avance.
                </p>
              ) : (
                <button
                  type="button"
                  className="z-mission-entryBtn"
                  onClick={() => setMissionOpen(true)}
                >
                  {blockView.buttonLabel || "Pasar a la acción"}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <MicroMissionModal
        open={!isReadOnly && missionOpen}
        onClose={closeMissionAndReturnToTop}
        missionData={microMissionData}
        missionProgress={data?.mission_progress}
        missionHistory={data?.mission_history || []}
        missionSummary={data?.mission_summary}
        missionDaySummary={data?.mission_day_summary}
        onComplete={completeMission}
      />

      <section className="z-day-navCard">
        <div className="z-day-navMeta">
          <span>Recorrido del día</span>
          <span>
            {step + 1} / {blocks.length || 1}
          </span>
        </div>

        <div className="z-day-navActions">
          <button
            type="button"
            className="z-day-navBtn z-day-navBtn--ghost"
            onClick={prev}
            disabled={step === 0}
          >
            Anterior
          </button>

          {step < blocks.length - 1 ? (
            <button
              type="button"
              className="z-day-navBtn z-day-navBtn--primary"
              onClick={next}
            >
              Continuar
              <ArrowIcon />
            </button>
          ) : isReadOnly ? (
            <button
              type="button"
              className="z-day-navBtn z-day-navBtn--primary"
              onClick={() => navigate(systemBackRoute)}
            >
              Volver al recorrido
              <ArrowIcon />
            </button>
          ) : (
            <button
              type="button"
              className="z-day-navBtn z-day-navBtn--primary"
              onClick={markCompleted}
              disabled={submitting || isCompleted}
            >
              {isCompleted ? (
                <>
                  Completado
                  <CheckIcon />
                </>
              ) : submitting ? (
                "Guardando…"
              ) : (
                <>
                  Marcar como completado
                  <CheckIcon />
                </>
              )}
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
